<template>
  <section style="width: 1200px">
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card" style="width: 1200px">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ title }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              horizontal
              name="status"
              attribute="attributes.status"
              :label="$t('attributes.status')"
            >
              <b-switch v-model="form.status" name="status"></b-switch>
            </form-group>

            <form-group horizontal name="topic" :label="$t('attributes.topic')">
              <b-input
                v-model="form.topic"
                name="topic"
                :placeholder="
                  $t('message.please_input') + $t('attributes.topic')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="type"
              :label="$t('attributes.edm_type')"
            >
              <b-select v-model="form.type" name="type" expanded>
                <option value="A">
                  {{ $t('attributes.edm_link_type') }}
                </option>
                <option value="B">
                  {{ $t('attributes.edm_normal_type') }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="issue_date"
              attribute="attributes.edm_issue_date"
              :label="$t('attributes.edm_issue_date')"
            >
              <b-datepicker
                v-model="issue_date"
                :placeholder="
                  $t('message.please_select') + $t('attributes.edm_issue_date')
                "
                trap-focus
                @input="onSelectedIssueDate"
              >
              </b-datepicker>
            </form-group>

            <form-group
              horizontal
              name="image"
              attribute="attributes.picture"
              grouped
            >
              <template #label>
                {{ $t('attributes.picture') }}
                <b-tooltip type="is-dark" label="360*200/限定jpg檔，100KB內">
                  <b-icon
                    size="is-small"
                    type="is-danger"
                    icon="help-circle-outline"
                  ></b-icon>
                </b-tooltip>
              </template>
              <b-field class="file">
                <a class="button is-primary" @click="onSelectPictureImage">
                  <b-icon icon="upload"></b-icon>
                  <span>{{ $t('message.please_select_file') }}</span>
                </a>
                <figure v-if="form.image" class="picture">
                  <a
                    :href="EdmImage(form.image)"
                    @click.prevent="onClickImage(form.image)"
                  >
                    <img
                      :src="EdmImage(form.image)"
                      :alt="$t('attributes.image')"
                      style="height: 40px; width: auto; margin-left: 6px;"
                    />
                  </a>
                </figure>
              </b-field>
            </form-group>

            <form-group horizontal name="url" :label="$t('attributes.url')">
              <b-input
                v-model="form.url"
                name="url"
                :disabled="form.type !== 'A'"
                :placeholder="$t('message.please_input') + $t('attributes.url')"
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="content"
              :label="$t('attributes.content')"
            >
              <div class="content">
                <editor
                  ref="content_editor"
                  v-model="form.content"
                  image-type="Edm"
                ></editor>
              </div>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="sending"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ isUpdate ? $t('attributes.update') : $t('attributes.add') }}
          </b-button>
        </footer>

        <b-modal :active.sync="isImageModalActive">
          <p class="image">
            <img :src="EdmImage(selectedImageUrl)" alt="" />
          </p>
        </b-modal>

        <b-loading
          :active.sync="isLoading"
          :is-full-page="false"
          :can-cancel="false"
        ></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
import { OpenEdmFinder } from '@/utils/ckfinder'
import notifyService from '@/services/notifyService'
import Editor from '@/components/editors/Editor4'

export default {
  name: 'EdmForm',
  components: { Editor },

  props: {
    edm: {
      type: Object,
      required: true
    }
  },
  data() {
    const isUpdate = !!this.edm.id
    const title = isUpdate
      ? this.$t('attributes.modify')
      : this.$t('attributes.add')

    const issue_date = this.edm.issue_date
      ? this.$dayjs(this.edm.issue_date).toDate()
      : this.$dayjs().toDate()

    return {
      isUpdate: isUpdate,
      title: title,

      issue_date: issue_date,

      sending: false,
      form: this.extractFormValues(this.edm),

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  validations: {
    form: {
      status: { required },
      type: { required },
      issue_date: { required },
      topic: { required, maxLength: maxLength(500) },
      image: { required, maxLength: maxLength(100) },
      url: {
        requiredIf: requiredIf(function(model) {
          return model.type === 'A'
        }),
        maxLength: maxLength(500)
      },
      content: {
        requiredIf: requiredIf(function(model) {
          return model.type === 'B'
        })
      }
    }
  },
  computed: {
    isLoading() {
      return this.sending
    }
  },
  methods: {
    extractFormValues(data) {
      this.issue_date = data.issue_date
        ? this.$dayjs(data.issue_date).toDate()
        : this.$dayjs().toDate()

      return {
        id: data.id,
        status: data.status,
        type: data.type,
        issue_date: this.formatDate(this.issue_date),
        topic: data.topic,
        url: data.url,
        image: data.image,
        content: data.content
      }
    },
    async onSelectPictureImage() {
      try {
        this.form.image = await OpenEdmFinder()
      } catch (e) {
        notifyService.error(e)
      }
    },
    onSelectedIssueDate() {
      this.form.issue_date = this.formatDate(this.issue_date)
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    },
    async onSend() {
      this.form.content = this.$refs.content_editor.getData()

      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
